/// <reference types="segment-analytics" />
import * as React from 'react';
import * as segment from '@segment/snippet';
import global from '~/libs/analytics/utils/global';
import { useMe } from '~/libs/api-hooks/api/private';
import exceptionLogger from '~/libs/exception-logger';
import { privacyOptOutIntegrations } from '~/libs/analytics/utils/options';

declare global {
  interface Window {
    analytics: SegmentAnalytics.AnalyticsJS;
  }
}

const getAnalyticsWriteKey = () => {
  return global.circleci?.config?.analyticsKey;
};

/**
 * Configures Analytics for the current user and enforces Privacy Opt Out.
 * @param me - The current user.
 */
const useSetSnippet = (isOuterPage = false) => {
  const [isSnippetSuccess, setIsSnippetSuccess] = React.useState(false);
  const [isError, setIsError] = React.useState(false);

  React.useLayoutEffect(() => {
    const analyticsKey = getAnalyticsWriteKey();
    if (!analyticsKey) {
      setIsError(true);
      exceptionLogger().error('there is no analytics key');
      return;
    }

    const options = {
      apiKey: analyticsKey,
      // note: the page option is disabled because it's limited to SSR
      // use: trackPage event to explicityle track page impressions
      page: false,
      load: false,
    };

    const snippet =
      process.env.NODE_ENV === 'development'
        ? segment.max(options)
        : segment.min({
            host: 'cci-growth-utils.s3.us-east-2.amazonaws.com/segment',
            ...options,
          });
    if (!window.analytics) {
      eval(snippet);
    }
    setIsSnippetSuccess(true);
  }, []);

  const {
    data: me,
    isLoading: meIsLoading,
    isError: meIsError,
    error: meError,
  } = useMe({
    reactQueryOptions: {
      retry: false,
      retryOnMount: false,
      enabled: !isOuterPage,
    },
  });

  const [isAnalyticsLoadSuccess, setIsAnalyticsLoadSuccess] =
    React.useState(false);

  // This is useLayoutEffect rather than useEffect because
  // we need our analytics ASAP
  React.useLayoutEffect(() => {
    if (
      !isSnippetSuccess ||
      (!isOuterPage && meIsLoading) ||
      isAnalyticsLoadSuccess
    ) {
      return;
    }
    const analyticsKey = getAnalyticsWriteKey();
    if (!analyticsKey) {
      setIsError(true);
      return;
    }
    const isErrorFetchingMe = meIsError && !meError?.isUnauthorizedError();
    if (isErrorFetchingMe) {
      exceptionLogger().error(
        'Unable load user data for analytics',
        meError ?? {},
      );
      // Treat user with privacyOptout = true so don't return
    }

    const segmentAnalyticsAlreadySetup =
      typeof window?.analytics?.load !== 'function';
    if (segmentAnalyticsAlreadySetup) {
      setIsAnalyticsLoadSuccess(true);
      return;
    }

    const options =
      me?.privacyOptout || isErrorFetchingMe
        ? {
            integrations: privacyOptOutIntegrations,
          }
        : {};

    window.analytics.load(analyticsKey, options);
    window.analytics.ready((): void => {
      setIsAnalyticsLoadSuccess(true);
    });
  }, [
    me?.privacyOptout,
    meError,
    meIsError,
    isSnippetSuccess,
    meIsLoading,
    isAnalyticsLoadSuccess,
    isOuterPage,
  ]);

  const analytics = React.useMemo(
    () =>
      isSnippetSuccess && isAnalyticsLoadSuccess ? window.analytics : undefined,
    [isAnalyticsLoadSuccess, isSnippetSuccess],
  );

  return {
    segment: analytics,
    isAnalyticsLoadSuccess,
    isSnippetSuccess,
    isError,
  };
};

export default useSetSnippet;
